import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Button, Dialog, Field, Select } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  IMPORTABLE_SUPPLIERS,
  ImportInvoiceClient,
  type ImportInvoiceDTO,
  type InvoiceExpenseSupplier,
} from "../../api/manualInvoice";
import { currencyFromId } from "../../api/part";
import { NexpimClientClient } from "../../api/pricing";
import type { SigmClientDTO } from "../../api/reporting";

/**
 * Manual invoices → Import: the imported expense-invoice list plus the
 * upload flow — an xlsx/csv feed posted raw with the supplier as a query
 * param; the response is a per-row error list, empty meaning full success.
 */
export function ImportInvoiceGrid({ toolbar }: { toolbar: ReactNode }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();

  const invoices = useMemo(() => new ImportInvoiceClient(http), [http]);
  const clients = useMemo(() => new NexpimClientClient(http), [http]);

  const [importOpen, setImportOpen] = useState(false);
  const [supplier, setSupplier] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState<string[] | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["manual-invoice", "import"],
    queryFn: () => invoices.findAll(),
    staleTime: 60_000,
  });

  const clientsQuery = useQuery({
    queryKey: ["client", "all"],
    queryFn: () => clients.findAll(),
    staleTime: 30 * 60_000,
  });

  const clientMap = useMemo(
    () => new Map((clientsQuery.data ?? []).map((client: SigmClientDTO) => [client.cli_id, client])),
    [clientsQuery.data],
  );

  const cols = useMemo(() => {
    const c = defineColumns<ImportInvoiceDTO>();
    return [
      c.text("supplier", (row) => row.invoice_supplier ?? "", {
        headerKey: "column.invoice-supplier",
        filter: "select",
        priority: 1,
      }),
      c.text("invoice_no", (row) => row.invoice_no ?? "", { headerKey: "column.invoice-no", priority: 2 }),
      c.text("invoice_date", (row) => row.invoice_date ?? "", {
        headerKey: "column.invoice-date",
        filter: "daterange",
        priority: 3,
      }),
      c.text("currency", (row) => currencyFromId(row.invoice_currency), {
        headerKey: "column.invoice-currency",
        filter: "select",
      }),
      c.text("category", (row) => row.invoice_expense_category ?? "", {
        headerKey: "column.expense-category",
        filter: "select",
      }),
      c.custom(
        "client",
        (row) => {
          const client = row.client_id ? clientMap.get(row.client_id) : undefined;
          return client ? `${client.cli_name1 ?? ""} ${client.cli_no ?? ""}` : "";
        },
        (row) => {
          const client = row.client_id ? clientMap.get(row.client_id) : undefined;
          if (!client) return <span />;
          return (
            <span>
              {client.cli_name1}
              <span className="ml-1 text-[11px] text-ink-faint">{client.cli_no}</span>
            </span>
          );
        },
        { headerKey: "column.client", priority: 3 },
      ),
      c.number("subtotal", (row) => row.subtotal ?? 0, { headerKey: "column.subtotal", priority: 3 }),
      c.number("tax_1", (row) => row.tax_1 ?? 0, { headerKey: "column.tax-1", defaultVisible: false }),
      c.number("tax_2", (row) => row.tax_2 ?? 0, { headerKey: "column.tax-2", defaultVisible: false }),
    ];
  }, [clientMap]);

  const runImport = async () => {
    if (!file || supplier === "") return;
    setImportOpen(false);
    setImporting(true);
    toast.info(t("notification.import-invoice.started", "Import started"));
    try {
      const errors = await invoices.importFeed(supplier as InvoiceExpenseSupplier, file);
      await queryClient.invalidateQueries({ queryKey: ["manual-invoice", "import"] });
      if (errors.length === 0) {
        toast.success(t("notification.import-invoice.success", "Invoices imported"));
      } else {
        setImportErrors(errors);
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("notification.import-invoice.error", "Import failed"),
      );
    } finally {
      setImporting(false);
      setFile(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  return (
    <>

      <DataTable
        data={data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        toolbarExtra={toolbar}
        loading={isLoading}
        actions={[
          {
            label: t("menu.item.import-invoices", "Import invoices"),
            icon: <Upload className="size-4" />,
            write: true,
            disabled: importing,
            onClick: () => setImportOpen(true),
          },
        ]}
        emptyMessage={t("table.empty", "Nothing to show")}
      />

      <Dialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title={t("dialog.import-invoice.title", "Import invoices")}
        actions={
          <>
            <Button variant="ghost" onClick={() => setImportOpen(false)}>
              {t("dialog.cancel", "Cancel")}
            </Button>
            <Button onClick={() => void runImport()} disabled={!file || supplier === ""}>
              {t("dialog.confirm.import", "Import")}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label={t("form.field.supplier", "Supplier")} htmlFor="import-supplier" required>
            <Select
              id="import-supplier"
              value={supplier}
              placeholder={t("form.field.supplier.placeholder", "Select supplier")}
              options={IMPORTABLE_SUPPLIERS.map((value) => ({ value, label: value }))}
              onChange={(event) => setSupplier(event.target.value)}
              className="h-8 w-full"
            />
          </Field>
          <Field label={t("form.field.upload", "File")} htmlFor="import-file" required>
            <input
              id="import-file"
              ref={fileInput}
              type="file"
              accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              className="block w-full text-[13px] file:mr-3 file:rounded file:border-0 file:bg-surface-sunk file:px-3 file:py-1.5 file:text-[13px]"
            />
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={importErrors !== null}
        onClose={() => setImportErrors(null)}
        title={t("manual-invoice.import.errors", "Import finished with problems")}
        description={t(
          "manual-invoice.import.errors.detail",
          "Rows that parsed were imported. The rest are listed below.",
        )}
        size="md"
        actions={
          <Button variant="primary" onClick={() => setImportErrors(null)}>
            {t("button.close", "Close")}
          </Button>
        }
      >
        <ul className="flex max-h-80 flex-col gap-1 overflow-y-auto">
          {(importErrors ?? []).map((problem, index) => (
            <li key={index} className="rounded bg-surface-sunk px-2 py-1 text-[12px] text-ink-muted">
              {problem}
            </li>
          ))}
        </ul>
      </Dialog>
    </>
  );
}
